<template>
    <div v-if="!loading">
        <div class="events-map-wrapper position-relative">
            <kendo-map ref="map"
                :center="nipCoords"
                :zoom="10"
                style="width: 100%; height: 500px;"
                @markerclick="onMarkerClick">

                <kendo-map-layer :type="'tile'"
                                 :url-template="'https://#= subdomain #.tile.openstreetmap.org/#= zoom #/#= x #/#= y #.png'"
                                 :subdomains="['a', 'b', 'c']"
                                 :attribution="'&copy; <a href=\'https://osm.org/copyright\'>OpenStreetMap contributors</a>'">
                </kendo-map-layer>

                <kendo-map-layer :type="'marker'"
                                 :data-source="datasource"
                                 shape="pin"
                                 >
                </kendo-map-layer>
            </kendo-map>

            <div class="events-map-hint badge badge-light">
                {{ $t('eventsMapCtrlZoomHint') }}
            </div>
        </div>

        <DetailsModal v-model="detailsModel" :show="showDetails" @onClose="onDetailsClose"></DetailsModal>
    </div>
</template>

<script>
    import DetailsModal from '@/components/dynamic/register/details.modal';
    import { RegisterFieldValueQueryFilter } from '@/models/enums';
    import { mapGetters } from 'vuex';

    export default {
        components: {
            DetailsModal,
        },
        data() {
            return {
                detailsModel: null,
                showDetails: false,
                datasource: {
                    transport: {
                        read: {
                            url: this.$store.getters.baseUrl + '/api/events/eventsMapData',
                            dataType: 'json',
                            type: 'GET',
                            contentType: 'application/json',
                            beforeSend: this.beforeSend
                        }
                    },
                    schema: {
                        data: ''
                    }
                },
                loading: false
                ,_ctrlWheelHandler: null
                ,_wheelTarget: null
            }
        },
        computed: {
            ...mapGetters(['authToken', 'nipCoords'])
        },
        methods: {
            getMapWidget() {
                const mapRef = this.$refs.map;
                if (!mapRef || typeof mapRef.kendoWidget !== 'function') {
                    return null;
                }

                return mapRef.kendoWidget();
            },
            setupCtrlWheelZoom() {
                const map = this.getMapWidget();
                const $ = window.kendo && window.kendo.jQuery ? window.kendo.jQuery : null;

                if (!map || !$ || !map.element) {
                    return;
                }

                // Вграденото поведение на Kendo Map винаги прави preventDefault на mousewheel,
                // което спира скрола на страницата. Искаме zoom само при натиснат Ctrl.
                const MOUSEWHEEL_EVENTS = 'DOMMouseScroll mousewheel';
                if (map._mousewheel) {
                    map.element.unbind(MOUSEWHEEL_EVENTS, map._mousewheel);
                } else {
                    map.element.unbind(MOUSEWHEEL_EVENTS);
                }

                // Премахваме предишен наш listener, ако има.
                if (this._wheelTarget && this._ctrlWheelHandler) {
                    this._wheelTarget.removeEventListener('wheel', this._ctrlWheelHandler, { capture: true });
                }

                const target = map.element && map.element[0] ? map.element[0] : null;
                if (!target) {
                    return;
                }

                const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

                this._wheelTarget = target;
                this._ctrlWheelHandler = (ev) => {
                    if (!ev || ev.ctrlKey !== true) {
                        // Без Ctrl не спираме скрола на страницата.
                        return;
                    }

                    // Ctrl+wheel по подразбиране zoom-ва браузъра – спираме го и zoom-ваме картата.
                    ev.preventDefault();
                    ev.stopPropagation();

                    const options = map.options || {};
                    const fromZoom = map.zoom();
                    const delta = ev.deltaY < 0 ? 1 : -1;
                    const toZoom = clamp(fromZoom + delta, options.minZoom, options.maxZoom);

                    if (options.zoomable !== false && toZoom !== fromZoom) {
                        if (!map.trigger('zoomStart', { originalEvent: ev })) {
                            const cursor = map.eventOffset(ev);
                            const location = map.viewToLocation(cursor);
                            const postZoom = map.locationToLayer(location, toZoom);
                            const origin = postZoom.translate(-cursor.x, -cursor.y);
                            map._zoomAround(origin, toZoom);
                            map.trigger('zoomEnd', { originalEvent: ev });
                        }
                    }
                };

                // Важно: passive:false, иначе Chrome игнорира preventDefault и браузърът zoom-ва.
                target.addEventListener('wheel', this._ctrlWheelHandler, { passive: false, capture: true });
            },
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            onMarkerClick(e) {
                console.log(e.marker.dataItem.registerEntityId);
                this.showDetailsModal(e.marker.dataItem.registerEntityId, RegisterFieldValueQueryFilter.ShowInDescription);
            },
            showDetailsModal(registerEntityId, filter) {
                this.$api.dynamicRegister
                    .getDetails(registerEntityId, filter)
                    .then(response => {
                        const model = response.data;
                        if (model) {
                            this.detailsModel = model;
                            this.detailsModel.isEvent = true;
                            this.showDetails = true;
                        }
                    })
                    .catch(error => {
                        this.detailsModel = null;
                        this.showDetails = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            onDetailsClose() {
                this.showDetails = false;
                this.detailsModel = null;
            },
        }
        ,mounted() {
            this.$nextTick(() => {
                this.setupCtrlWheelZoom();
            });
        }
        ,beforeDestroy() {
            const map = this.getMapWidget();
            if (this._wheelTarget && this._ctrlWheelHandler) {
                this._wheelTarget.removeEventListener('wheel', this._ctrlWheelHandler, { capture: true });
            }
            if (map && map.element && map._mousewheel) {
                // Няма да го връзваме обратно – целта е да не блокира скрола.
                map.element.unbind('DOMMouseScroll mousewheel', map._mousewheel);
            }
            this._ctrlWheelHandler = null;
            this._wheelTarget = null;
        }
    }
</script>

<style scoped>
.events-map-hint {
    position: absolute;
    left: 10px;
    bottom: 10px;
    z-index: 2;
    pointer-events: none;
}
</style>

